import React, { useEffect, useState } from 'react';
import { analyticsApi, adminApi } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../../components/common/Spinner';
import StatCard from '../../components/common/StatCard';
import { FaChartBar, FaCalendarCheck, FaTrophy, FaFilePdf } from 'react-icons/fa';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';

const gradeColor = v => v >= 75 ? '#10b981' : v >= 50 ? '#f59e0b' : '#ef4444';
const gradeSymbol = v => v >= 80?'A':v >= 70?'B':v >= 60?'C':v >= 50?'D':v >= 40?'E':'F';

export default function LearnerAnalytics() {
  const { user } = useAuth();
  const [data,    setData]    = useState(null);
  const [terms,   setTerms]   = useState([]);
  const [termId,  setTermId]  = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { adminApi.getTerms().then(r => setTerms(r.data.data)).catch(()=>{}); }, []);

  useEffect(() => {
    setLoading(true);
    analyticsApi.getLearner(user.id, termId || 0)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [termId, user.id]);

  const radarData = data?.by_subject?.slice(0,8).map(s => ({
    subject: s.subject_name.length > 10 ? s.subject_name.slice(0,10)+'…' : s.subject_name,
    avg: parseFloat(s.avg_pct) || 0,
  }));

  const openReportCard = () => {
    const url = analyticsApi.getReportCard(user.id, termId || 0);
    window.open(url, '_blank');
  };

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h2>My Performance</h2><p>Your academic progress and analytics</p></div>
        <div style={{ display:'flex', gap:10 }}>
          <select className="sms-input" style={{width:160}} value={termId} onChange={e=>setTermId(e.target.value)}>
            <option value="">All Terms</option>
            {terms.map(t=><option key={t.term_id} value={t.term_id}>{t.name} {t.year}</option>)}
          </select>
          <button onClick={openReportCard} className="sms-btn sms-btn-outline" style={{color:'#0d6efd'}}>
            <FaFilePdf size={13}/> Report Card
          </button>
        </div>
      </div>

      {loading ? <Spinner /> : !data || !data.by_subject?.length ? (
        <div className="sms-card" style={{textAlign:'center',color:'#9ca3af',padding:'60px 0'}}>
          No marks recorded yet
        </div>
      ) : (
        <>
          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:16, marginBottom:28 }}>
            <StatCard label="Overall Average" value={data.overall_avg ? `${data.overall_avg}%` : '—'} icon={FaChartBar}
              color={gradeColor(data.overall_avg)} sub={gradeSymbol(data.overall_avg)} />
            <StatCard label="Class Rank" value={data.rank ? `#${data.rank}` : '—'} icon={FaTrophy}
              color="#f59e0b" sub={`of ${data.class_size} learners`} />
            <StatCard label="Attendance" value={data.attendance?.pct !== undefined ? `${data.attendance.pct}%` : '—'} icon={FaCalendarCheck}
              color={gradeColor(data.attendance?.pct)} sub={`${data.attendance?.present}/${data.attendance?.total} days`} />
            <StatCard label="Subjects" value={data.by_subject?.length ?? 0} icon={FaChartBar} color="#8b5cf6" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, marginBottom:24 }}>
            {/* Radar */}
            <div className="sms-card">
              <h5 style={{fontWeight:700,marginBottom:16}}>Performance by Subject</h5>
              {radarData?.length > 2 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" tick={{fontSize:10}} />
                    <Radar dataKey="avg" stroke="#0d6efd" fill="#0d6efd" fillOpacity={0.25} name="Average %" />
                    <Tooltip formatter={v=>[`${v}%`,'Avg']} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{marginTop:8}}>
                  {data.by_subject?.map(s=>{
                    const avg = parseFloat(s.avg_pct);
                    const col = gradeColor(avg);
                    return (
                      <div key={s.subject_id} style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                        <div style={{flex:1,fontSize:'0.85rem',fontWeight:500}}>{s.subject_name}</div>
                        <div style={{width:120,height:8,background:'#f3f4f6',borderRadius:4,overflow:'hidden'}}>
                          <div style={{width:`${avg}%`,height:'100%',background:col,borderRadius:4,transition:'width 0.5s'}} />
                        </div>
                        <div style={{fontWeight:700,color:col,minWidth:40,textAlign:'right'}}>{avg}%</div>
                        <div style={{fontWeight:800,color:col,minWidth:20,textAlign:'center'}}>{gradeSymbol(avg)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Attendance monthly */}
            <div className="sms-card">
              <h5 style={{fontWeight:700,marginBottom:16}}>Attendance (Last 6 Months)</h5>
              {data.attendance_monthly?.length > 1 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.attendance_monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" tick={{fontSize:10}} />
                    <YAxis tick={{fontSize:10}} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="present" stroke="#10b981" strokeWidth={2} name="Present" dot={false} />
                    <Line type="monotone" dataKey="absent"  stroke="#ef4444" strokeWidth={2} name="Absent"  dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p style={{color:'#9ca3af',textAlign:'center',paddingTop:60}}>Not enough data yet</p>}
            </div>
          </div>

          {/* History table */}
          <div className="sms-card" style={{padding:0,overflow:'hidden'}}>
            <div style={{padding:'16px 20px',fontWeight:700,borderBottom:'1px solid #e5e7eb'}}>Assessment History</div>
            <table className="sms-table">
              <thead><tr><th>Date</th><th>Subject</th><th>Assessment</th><th>Type</th><th>Score</th><th>%</th><th>Comment</th></tr></thead>
              <tbody>
                {data.history?.map((h,i)=>{
                  const col = gradeColor(parseFloat(h.pct));
                  return (
                    <tr key={i}>
                      <td style={{color:'#6b7280',fontSize:'0.82rem'}}>{h.date||'—'}</td>
                      <td style={{fontWeight:600}}>{h.subject_name}</td>
                      <td>{h.assessment_name}</td>
                      <td style={{textTransform:'capitalize',fontSize:'0.82rem'}}>{h.type}</td>
                      <td>{h.score}/{h.total_marks}</td>
                      <td><span style={{fontWeight:700,color:col}}>{h.pct}%</span></td>
                      <td style={{color:'#6b7280',fontSize:'0.82rem'}}>{h.comment||'—'}</td>
                    </tr>
                  );
                })}
                {!data.history?.length && <tr><td colSpan="7" style={{textAlign:'center',color:'#9ca3af',padding:'30px 0'}}>No assessment history</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
